import * as React from 'react';
import {
  AlertCircle,
  AlertTriangle,
  Info,
  ChevronRight,
  X,
} from 'lucide-react';
import { cn } from '../../lib/utils';

export interface AlarmCounts {
  critical?: number;
  major?: number;
  minor?: number;
  advisory?: number;
}

export interface AlarmRibbonProps {
  counts: AlarmCounts;
  /** Page-scoped label (e.g. "Plant A · Active alarms"). */
  scope?: string;
  /** Click handler (typically: navigate to alarms list). */
  onSelect?: () => void;
  /** Optional dismiss handler (X button). */
  onDismiss?: () => void;
  className?: string;
}

/**
 * Tyndo AlarmRibbon — page-scoped severity stripe (separate from the global one).
 *
 * Drop above content on Production / SCADA / Equipment / Quality pages where
 * a page-local set of alarms (filtered to that scope) deserves emphasis.
 * Colour stripe + chip strip + click-through.
 */
export function AlarmRibbon({
  counts,
  scope,
  onSelect,
  onDismiss,
  className,
}: AlarmRibbonProps) {
  const total =
    (counts.critical ?? 0) +
    (counts.major ?? 0) +
    (counts.minor ?? 0) +
    (counts.advisory ?? 0);

  if (total === 0) return null;

  const dominant: 'critical' | 'major' | 'minor' | 'advisory' =
    (counts.critical ?? 0) > 0
      ? 'critical'
      : (counts.major ?? 0) > 0
        ? 'major'
        : (counts.minor ?? 0) > 0
          ? 'minor'
          : 'advisory';

  const stripeColor = `hsl(var(--alarm-${dominant}))`;

  return (
    <div
      role="alert"
      className={cn(
        'group relative flex items-center gap-3 overflow-hidden rounded-md border border-border bg-surface-raised px-3 py-2 transition-colors',
        onSelect && 'cursor-pointer hover:bg-accent/40',
        className
      )}
    >
      {onSelect && (
        // Full-ribbon click/keyboard target. The overlay keeps the wrapper a
        // pure `role="alert"` live region while making the whole ribbon
        // activatable; the dismiss button stacks above it.
        <span
          role="button"
          tabIndex={0}
          aria-label="Open alarm list"
          onClick={onSelect}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onSelect();
            }
          }}
          className="absolute inset-0 z-[1] rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      )}
      <span
        aria-hidden
        className="absolute inset-y-0 left-0 w-1"
        style={{
          background: `linear-gradient(180deg, ${stripeColor} 0%, ${stripeColor.replace(')', ' / 0.5)')} 100%)`,
        }}
      />
      <span className="ml-1 inline-flex items-center gap-1.5 text-sm font-semibold text-foreground">
        <AlertTriangle
          className="h-3.5 w-3.5"
          style={{ color: stripeColor }}
        />
        {total} active alarm{total === 1 ? '' : 's'}
      </span>
      {scope && (
        <span className="text-xs text-muted-foreground">{scope}</span>
      )}
      <div className="ml-2 flex items-center gap-1.5">
        {(counts.critical ?? 0) > 0 && (
          <Chip label="Critical" count={counts.critical!} color="alarm-critical" Icon={AlertCircle} />
        )}
        {(counts.major ?? 0) > 0 && (
          <Chip label="Major" count={counts.major!} color="alarm-major" Icon={AlertTriangle} />
        )}
        {(counts.minor ?? 0) > 0 && (
          <Chip label="Minor" count={counts.minor!} color="alarm-minor" Icon={AlertTriangle} />
        )}
        {(counts.advisory ?? 0) > 0 && (
          <Chip label="Advisory" count={counts.advisory!} color="alarm-advisory" Icon={Info} />
        )}
      </div>
      {onSelect && (
        <span className="ml-auto inline-flex items-center gap-1 text-[11px] text-muted-foreground transition-opacity opacity-0 group-hover:opacity-100">
          open
          <ChevronRight className="h-3 w-3" />
        </span>
      )}
      {onDismiss && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDismiss();
          }}
          className="relative z-[2] rounded p-1 text-muted-foreground hover:bg-foreground/10 hover:text-foreground"
          aria-label="Dismiss"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

function Chip({
  label,
  count,
  color,
  Icon,
}: {
  label: string;
  count: number;
  color: 'alarm-critical' | 'alarm-major' | 'alarm-minor' | 'alarm-advisory';
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
}) {
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium"
      style={{
        backgroundColor: `hsl(var(--${color}) / 0.14)`,
        color: `hsl(var(--${color}))`,
        boxShadow: `inset 0 0 0 1px hsl(var(--${color}) / 0.3)`,
      }}
    >
      <Icon className="h-3 w-3" />
      <span>{label}</span>
      <span className="t-tabular tabular-nums font-semibold">{count}</span>
    </span>
  );
}
